import React, { Fragment, Component } from 'react'
import PropTypes from 'prop-types'
import track from 'react-tracking'

import BEMHelper from '@ynap/bem-helper'
import { CreditCardForm } from '@ynap/credit-card-form'
import { Step } from '@ynap/multi-step-form'
import PaymentCard from '@ynap/payment-card'
import { OptimizelyFeature } from '@ynap/optimizely-utils'

import { startApplePaySession } from '../../utils/applePaySession'
import { cleanAddressEmptyValues } from '../../utils/formatting'
import { MetaData } from '../../utils/getMetaData'
import { isPaymentEnabled, isSelectedMethodConfigDisabled } from '../../utils/payment-utils'
import createAnalyticsEvent, { trackBuyNow, trackProceedToPurchaseApplePay, trackCancelApplePay, trackErrorApplePay } from '../analytics'
import GiftCardOption from '../PaymentCodes/GiftCardOption'
import PaymentOptionsForm from '../PaymentOptionsForm/PaymentOptionsForm'
import PremierToastNotification from './PremierToastNotification'

const bem = new BEMHelper('PaymentStep')
@track()
class PaymentStep extends Component {
    static propTypes = {
        config: PropTypes.object,
        messages: PropTypes.object,
        path: PropTypes.string,
        locale: PropTypes.object.isRequired,
        currentShippingAddress: PropTypes.object,
        currentBillingAddress: PropTypes.object,
        paymentOptions: PropTypes.array,
        savedAddresses: PropTypes.array,
        setPaymentMethod: PropTypes.func.isRequired,
        addAddress: PropTypes.func.isRequired,
        disabled: PropTypes.bool,
        completed: PropTypes.bool,
        cvvRequired: PropTypes.bool,
        onChange: PropTypes.func.isRequired,
        userStatus: PropTypes.string,
        currentCard: PropTypes.object,
        nextPath: PropTypes.string,
        returnUrl: PropTypes.string,
        totals: PropTypes.shape({
            packaging: PropTypes.string,
            currency: PropTypes.string.isRequired,
            itemTotal: PropTypes.string,
            shipping: PropTypes.string,
            total: PropTypes.string.isRequired,
            promotions: PropTypes.array.isRequired,
            savingsTotal: PropTypes.string,
        }).isRequired,
        setCheckoutNotification: PropTypes.func.isRequired,
        paypalBAEnabled: PropTypes.bool,
        deviceType: PropTypes.bool,
        canMakeApplePayments: PropTypes.bool,
        getApplePayRequest: PropTypes.func,
        handleStepClick: PropTypes.func.isRequired,
        hasShippingMethodChanged: PropTypes.bool,
        loyaltyInfo: PropTypes.shape({
            loyaltySegment: PropTypes.string,
        }),
        editAddress: PropTypes.func,
    }

    static defaultProps = {
        cvvRequired: false,
        config: {
            head: { flags: {} },
        },
    }

    constructor(props) {
        super(props)
        this.state = {
            addressFormTouched: false,
            premierToastNotificationTriggered: false,
            userHasApplePaySavedCards: undefined,
            isEIP: false,
        }
        this.renderForm = this.renderForm.bind(this)
    }

    async componentDidMount() {
        const { paymentOptions, fetchCart } = this.props

        if (!paymentOptions) {
            fetchCart()
        }

        const isEIP = window.SF?.getEipStatus && (await window.SF?.getEipStatus())?.isEIP
        this.setState({ isEIP })
    }

    async componentDidUpdate(prevProps) {
        const { isPremierSelected, isPremierAvailable, disabled, locale, history, messages, config, tracking, handleStepClick, hasShippingMethodChanged } =
            this.props
        const { premierToastNotificationTriggered } = this.state
        const isPremierToastNotificationEnabled = !!config?.head?.flags.isPremierToastNotificationEnabled

        if (isPremierToastNotificationEnabled && (prevProps.isPremierAvailable !== isPremierAvailable || prevProps.isPremierSelected !== isPremierSelected)) {
            if (isPremierAvailable && !isPremierSelected && !premierToastNotificationTriggered && !disabled) {
                const isEIP = window.SF?.getEipStatus && (await window.SF?.getEipStatus())?.isEIP
                window.SF?.setToastNotification?.(() => (
                    <PremierToastNotification isEIP={isEIP} messages={messages} locale={locale} history={history} tracking={tracking} />
                ))
                this.setState({ premierToastNotificationTriggered: true })
            }
        }

        if (prevProps.hasShippingMethodChanged !== hasShippingMethodChanged) {
            handleStepClick(!hasShippingMethodChanged)
        }
    }

    isBlockingRectification(cart) {
        return (cart?.shoppingCart?.orderMessages || []).find((orderMessage) => {
            return orderMessage?.code === 'SHIPPING_METHOD_INVALID'
        })
    }

    renderForm(props, disableKlarna) {
        const {
            config,
            messages,
            currentShippingAddress,
            currentShippingOption,
            currentBillingAddress,
            paymentOptions = [],
            savedAddresses = [],
            setPaymentMethod,
            addAddress,
            nextPath,
            returnUrl,
            userStatus,
            paymentRequired,
            checkoutError,
            totals,
            setCheckoutNotification,
            tracking,
            countries,
            fetchProvinces,
            fetchAllProvinces,
            fetchCountries,
            locale = {},
            headerElement,
            paypalBAEnabled,
            deviceType,
            addAddressError,
            checkout,
            history,
            dduInfo,
            cvvRequired,
            setCheckoutError,
            updatePaymentMethod,
            getApplePayRequest,
            checkoutAnalytics,
            optimizely,
            paymentMethodError,
            loyaltyInfo,
            editAddress,
        } = props
        const { country } = locale

        const initialAddressValues = { country, addressType: 'ShippingAndBilling' }

        const applePayParams = {
            setCheckoutError,
            updatePaymentMethod,
            checkout,
            history,
            redirect: nextPath,
            trackCancelApplePay: () => tracking.trackEvent(trackCancelApplePay(checkoutAnalytics)),
            trackErrorApplePay: (error) => tracking.trackEvent(trackErrorApplePay(checkoutAnalytics, error)),
            trackProceedToPurchaseApplePay: () => tracking.trackEvent(trackProceedToPurchaseApplePay(checkoutAnalytics)),
        }

        function getPaymentMethods(
            props,
            initialAddressValues,
        ) {
            const { config, locale, canMakeApplePayments, paymentOptions = [], currentBillingAddress, currentShippingAddress } = props

            const selectedPaymentOption = paymentOptions.find(({ selected }) => selected)

            const configEnabledPaymentMethods = [
                {
                    name: 'APPLEPAY',
                    enabled: isPaymentEnabled(config?.head?.applepay, locale?.country, canMakeApplePayments || false),
                },
                {
                    name: 'KLARNA',
                    enabled: Boolean(!disableKlarna && config?.head?.klarna?.enabled),
                },
                {
                    name: 'AFFIRM',
                    enabled: Boolean(config?.head?.affirm?.enabled),
                },
            ]

            const currentPaymentOption = isSelectedMethodConfigDisabled(selectedPaymentOption, configEnabledPaymentMethods) 
                ? paymentOptions.find((option) => option.id === 'CREDIT_CARD') 
                : selectedPaymentOption

            const initialValues = {
                paymentMethodId: currentPaymentOption ? currentPaymentOption.id : null,
                billingAddress: currentBillingAddress || {
                    addressId: currentShippingAddress && currentShippingAddress.addressId,
                    ...initialAddressValues,
                },
                returnUrl,
            }

            return [configEnabledPaymentMethods, initialValues]
        }

        let [configEnabledPaymentMethods, initialValues] = getPaymentMethods(props, initialAddressValues)

        const trackSubmitEvent = trackBuyNow
        return (
            <Fragment>
                <GiftCardOption messages={messages} />
                    <PaymentOptionsForm
                        addressFormTouched={this.state.addressFormTouched}
                        config={config}
                        childRef={(ref) => {
                            this.childRef = ref
                        }}
                        messages={messages}
                        paymentOptions={paymentOptions}
                        savedAddresses={savedAddresses}
                        currentShippingAddress={currentShippingAddress}
                        currentBillingAddress={currentBillingAddress}
                        initialValues={{
                            ...initialValues,
                            billingAddress: cleanAddressEmptyValues(initialValues?.billingAddress),
                        }}
                        initialAddressValues={initialAddressValues}
                        onFormSubmit={(values) => {
                            const { paymentMethodId, applePaySession } = values

                            tracking.trackEvent(
                                trackSubmitEvent({
                                    checkoutAnalytics: { ...checkoutAnalytics, paymentOption: [paymentMethodId] },
                                })
                            )

                            return setPaymentMethod(values, null).then((data) => {
                                if (!this.isBlockingRectification(data)) {
                                    applePaySession
                                        ? startApplePaySession(applePaySession, applePayParams)
                                        : checkout(returnUrl).then(() => history.push(nextPath))
                                }
                            })
                        }}
                        onSubmit={() => { }}
                        addAddress={addAddress}
                        enableReinitialize
                        keepDirtyOnReinitialize
                        isGuestUser={userStatus !== 'R'}
                        addCreditCard={(values) => setPaymentMethod({ ...initialValues, paymentMethodId: 'CREDIT_CARD', creditCard: values })}
                        paymentRequired={paymentRequired}
                        checkoutError={checkoutError}
                        countries={countries}
                        fetchProvinces={fetchProvinces}
                        fetchAllProvinces={fetchAllProvinces}
                        fetchCountries={fetchCountries}
                        locale={locale}
                        headerElement={headerElement}
                        paypalBAEnabled={paypalBAEnabled}
                        deviceType={deviceType}
                        addAddressError={addAddressError}
                        totals={totals}
                        dduInfo={dduInfo}
                        cvvRequired={cvvRequired}
                        currentShippingOption={currentShippingOption}
                        applePayRequest={getApplePayRequest?.()}
                        configEnabledPaymentMethods={configEnabledPaymentMethods}
                        checkoutAnalytics={checkoutAnalytics}
                        optimizely={optimizely}
                        isEIP={this.state.isEIP}
                        paymentMethodError={paymentMethodError}
                        loyaltyInfo={loyaltyInfo}
                        editAddress={editAddress}
                        setCheckoutNotification={setCheckoutNotification}
                    />
            </Fragment>
        )
    }

    renderSummary({ paymentOptions = [], cvvRequired, currentBillingAddress, cardTypeInfo, messages }) {
        const currentPaymentOption = paymentOptions.find(({ selected, id }) => selected && id !== 'STORE_CREDIT')
        const storeCredit = paymentOptions.find(({ id }) => id === 'STORE_CREDIT')
        const isCashOnDelivery = currentPaymentOption?.id === 'COD'
        const codPrice = currentPaymentOption?.price
        const showCVV = currentPaymentOption?.cardId && cvvRequired
        const addressLine = currentBillingAddress?.addressLine?.filter(Boolean).join(', ')

        let cardHolderName = ''
        if (currentPaymentOption?.card_holder) {
            const { name, first_name, last_name } = currentPaymentOption.card_holder
            cardHolderName = name || `${first_name} ${last_name}`
        } else if (currentBillingAddress?.firstName && currentBillingAddress?.lastName) {
            cardHolderName = `${currentBillingAddress.firstName} ${currentBillingAddress.lastName}`
        }

        const tryRemoveCharsAfterSlash = (id) => (id?.includes('/') ? id.split('/')[0] : id)

        const card = tryRemoveCharsAfterSlash(currentPaymentOption?.type || currentPaymentOption?.id)
            ?.replace('_', '')
            ?.toLowerCase()

        return (
            <Fragment>
                <div className={bem('paymentTypes')}>
                    {isCashOnDelivery && (
                        <Fragment>
                            <p className={bem('cod')}>{messages.paymentOptionsForm.cashOnDelivery()}</p>
                            <span className={bem('codAmount')}>{`- ${codPrice}`}</span>
                        </Fragment>
                    )}
                    {storeCredit && (
                        <div className={bem('store-credit')}>
                            <span className={bem('store-credit-message')}>{messages.paymentOptionsForm.storeCredit.label()}</span>
                            <span className={bem('store-credit-amount')}>{messages.paymentOptionsForm.storeCredit.amount({ amount: storeCredit.amount })}</span>
                        </div>
                    )}
                    {!isCashOnDelivery && currentPaymentOption && (
                        <div>
                            <PaymentCard className={bem('cardType', currentPaymentOption.type)} card={card} key={currentPaymentOption.type} />

                            {cardHolderName && <span className={bem('cardHoldersName')}>{`${cardHolderName}`}</span>}
                            {currentPaymentOption.cardId && (
                                <span className={bem('cardDetails')}>{messages.paymentOptionsForm.savedCardLabel(currentPaymentOption)}</span>
                            )}
                        </div>
                    )}
                </div>
                <p className={bem('cardAddress')}>{messages.paymentOptionsForm.billingTo({ addressLine })}</p>
                {showCVV && <CreditCardForm form="cvv" showCvvOnly cardTypeInfo={cardTypeInfo} onSubmit={() => { }} messages={messages} />}
            </Fragment>
        )
    }

    @track((args) => {
        const { checkoutAnalytics, previousStepsComplete } = args
        return (
            previousStepsComplete &&
            createAnalyticsEvent({
                ...checkoutAnalytics,
                pageConfigs: {
                    pageName: 'checkout - payment options',
                    pageType: 'checkout',
                    primaryCategory: 'purchase',
                    subCategory1: 'checkout',
                    subCategory2: 'payment options',
                },
            })
        )
    })
    trackPage() { }

    render() {
        const { locale, messages, config, ...rest } = this.props
        const invisible = !this.props.location?.pathname?.includes('/checkout/payment-options')

        return (
            <OptimizelyFeature feature='checkout_optimizely_test'>
                {() => (
                    <>
                        {!invisible && <MetaData title={messages.paymentOptionsForm.title()} brandId={config.global.application.brandId} />}
                        <OptimizelyFeature feature={['WEB00712_noklarna_cart_web_mob', 'WEB00713_noklarna_cart_web_mob']}>
                            {(props) => {
                                const { disableKlarna } = props.variables || {}

                                return (
                                    <Step
                                        className={bem(messages.paymentOptionsForm.title())}
                                        title={messages.paymentOptionsForm.title()}
                                        form={() => this.renderForm(this.props, disableKlarna)}
                                        summary={this.renderSummary}
                                        locale={locale}
                                        messages={messages}
                                        pageAnalyticsStep="payment options"
                                        pageAnalyticsFn={this.trackPage.bind(this)}
                                        invisible={invisible}
                                        config={config}
                                        isCheckoutLayout={true}
                                        {...rest}
                                    />
                                )
                            }}
                        </OptimizelyFeature>
                    </>
                )}
            </OptimizelyFeature>
        )
    }
}

export default PaymentStep
